import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expired, try to refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const { data } = await axios.post(`${API_URL}/auth/refresh`, {
            refreshToken,
          });
          localStorage.setItem('token', data.data.token);
          // Retry original request
          error.config.headers.Authorization = `Bearer ${data.data.token}`;
          return axios(error.config);
        } catch (refreshError) {
          // Refresh failed, clear tokens but don't redirect
          // Let the calling code handle the error
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      } else {
        // No refresh token, clear token but don't redirect
        // Let the calling code handle the error
        localStorage.removeItem('token');
      }
    }
    return Promise.reject(error);
  }
);

// Authentication
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  refreshToken: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  sendVerificationCode: (data) => api.post('/auth/send-verification-code', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  sendPasswordResetCode: (data) => api.post('/auth/send-password-reset-code', data),
  resetPasswordWithCode: (data) => api.post('/auth/reset-password-with-code', data),
};

// User
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  uploadAvatar: (formData) => api.post('/users/avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }),
  changePassword: (data) => api.put('/users/password', data),
  getSubscription: () => api.get('/users/subscription'),
  getUsage: () => api.get('/users/usage'),
};

// Content
export const contentAPI = {
  generate: (data) => api.post('/content/generate', data),
  getHistory: (params) => api.get('/content/history', { params }),
  getFavorites: () => api.get('/content/favorites'),
  toggleFavorite: (id) => api.post(`/content/${id}/favorite`),
  deleteContent: (id) => api.delete(`/content/${id}`),

  // Get available AI models
  getAvailableModels: () => api.get('/content/models'),
  getModels: () => api.get('/content/models'),

  // AI Avatar Chat - with extended timeout for AI response
  chatWithAIAvatar: (data) => api.post('/content/ai-avatar/chat', data, { timeout: 120000 }), // 120 seconds (synchronous)

  // AI Avatar Chat - Async task-based (non-blocking, survives page refresh)
  createChatTask: (data) => api.post('/content/ai-avatar/task', data),
  getChatTask: (taskId) => api.get(`/content/ai-avatar/task/${taskId}`),
  getUserChatTasks: (params) => api.get('/content/ai-avatar/tasks', { params }),

  // AI Avatar Conversations - User-isolated conversation storage
  getConversations: (params) => api.get('/content/ai-avatar/conversations', { params }),
  createConversation: (data) => api.post('/content/ai-avatar/conversations', data),
  updateConversation: (id, data) => api.put(`/content/ai-avatar/conversations/${id}`, data),
  deleteConversation: (id) => api.delete(`/content/ai-avatar/conversations/${id}`),

  // Speech Synthesis (Edge TTS)
  generateSpeech: (data) => api.post('/content/speech/generate', data),
  generateSpeechStreaming: async (data, onChunk, onComplete, onError, onSegment, onInfo) => {
    try {
      const token = localStorage.getItem('token');
      const baseURL = 'https://api.getaizest.com/api';

      const response = await fetch(`${baseURL}/content/speech/generate/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n\n');
        buffer = lines.pop(); // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          const [eventLine, dataLine] = line.split('\n');

          if (!eventLine || !dataLine) continue;

          const event = eventLine.replace('event: ', '').trim();
          const dataStr = dataLine.replace('data: ', '').trim();

          try {
            const data = JSON.parse(dataStr);

            if (event === 'chunk' && onChunk) {
              onChunk(data);
            } else if (event === 'complete' && onComplete) {
              onComplete(data);
            } else if (event === 'error' && onError) {
              onError(new Error(data.error || 'Stream error'));
            } else if (event === 'segment' && onSegment) {
              onSegment(data);
            } else if (event === 'info' && onInfo) {
              onInfo(data);
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError, dataStr);
          }
        }
      }
    } catch (error) {
      if (onError) {
        onError(error);
      }
      throw error;
    }
  },
  listVoices: (language = 'en') => api.get('/content/speech/voices', { params: { language } }),
  getDefaultVoices: () => api.get('/content/speech/default-voices'),
  testEdgeTts: () => api.get('/content/speech/test'),
  getVoices: () => api.get('/content/speech/voices'), // Get all available TTS voices

  // Ad Copy Generation
  generateAdCopy: (data) => api.post('/content/ad-copy/generate', data, { timeout: 180000 }), // 180 seconds for complex generation

  // Email Marketing Generation
  generateEmailMarketing: (data) => api.post('/content/email-marketing/generate', data, { timeout: 180000 }), // 180 seconds for comprehensive email
  beautifyEmail: (data) => api.post('/content/email/beautify', data, { timeout: 180000 }), // 180 seconds for email beautification
  replyToEmail: (data) => api.post('/content/email/reply', data, { timeout: 180000 }), // 180 seconds for email reply generation

  // Video Script Generation
  generateVideoScript: (data) => api.post('/content/video-script/generate', data, { timeout: 1800000 }), // 1800 seconds (30 min) for video scripts

  // Blog Posts Generation
  generateBlogPost: (data) => api.post('/content/blog-post/generate', data, { timeout: 1800000 }), // 1800 seconds (30 min) for blog posts with images

  // Product Descriptions Generation
  generateProductDescription: (data) => api.post('/content/product-description/generate', data, { timeout: 1800000 }), // 1800 seconds (30 min) for product descriptions

  // Image Composition
  composeImages: (formData) => {
    const token = localStorage.getItem('token');
    return axios.post('https://api.getaizest.com/api/content/image-composition/compose', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${token}`
      },
      timeout: 300000 // 300 seconds for image processing
    });
  },

  // AI Image Generation
  generateImage: (data) => api.post('/content/image-generation/generate', data, { timeout: 300000 }), // 300 seconds for image generation
  generateImageToImage: (formData) => api.post('/content/image-generation/image-to-image', formData, {
    timeout: 300000,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }), // 300 seconds for image-to-image generation
  optimizeImagePrompt: (data) => api.post('/content/image-generation/optimize-prompt', data, { timeout: 60000 }), // 60 seconds for prompt optimization
  optimizeAdDescription: (data) => api.post('/content/ad-copy/optimize-description', data, { timeout: 60000 }), // 60 seconds for description optimization

  // Poster Generation (Alibaba Cloud DashScope)
  generatePoster: (data) => api.post('/content/poster/generate', data, { timeout: 300000 }), // 300 seconds for poster generation
  optimizePosterTitle: (data) => api.post('/content/poster/optimize-title', data, { timeout: 60000 }), // 60 seconds for title optimization
  optimizePosterPrompt: (data) => api.post('/content/poster/optimize-prompt', data, { timeout: 60000 }), // 60 seconds for prompt optimization

  // Market Survey Generation (uses MCP tools + Qwen3-VL-Plus)
  generateMarketSurvey: (data) => api.post('/content/market-survey/generate', data, { timeout: 600000 }), // 600 seconds (10 minutes) for comprehensive research with multiple MCP searches
  updateMarketSurvey: (id, data) => api.put(`/content/market-survey/${id}`, data, { timeout: 60000 }), // Update market survey report
};

// Subscription
export const subscriptionAPI = {
  getPlans: () => api.get('/subscription/plans'),
  subscribe: (data) => api.post('/subscription/subscribe', data),
  cancel: () => api.post('/subscription/cancel'),
  upgrade: (data) => api.post('/subscription/upgrade', data),
  getPortalUrl: () => api.get('/subscription/portal'),
};

// Payment
export const paymentAPI = {
  createIntent: (data) => api.post('/payment/create-intent', data),
  confirmPayment: (data) => api.post('/payment/confirm', data),
  getHistory: () => api.get('/payment/history'),
};

// Stripe
export const stripeAPI = {
  createCheckoutSession: (data) => api.post('/stripe/create-checkout-session', data),
  createPortalSession: () => api.post('/stripe/create-portal-session'),
  getCheckoutSession: (sessionId) => api.get(`/stripe/checkout-session/${sessionId}`),
  cancelSubscription: (data) => api.post('/stripe/cancel-subscription', data),
  reactivateSubscription: () => api.post('/stripe/reactivate-subscription'),
  getInvoices: () => api.get('/stripe/invoices'),
};

export default api;
